import type { TextElement } from '@/modules/editor/canvas/element';
import { PixiGlobal } from '@/modules/editor/canvas/PixiGlobal';
import type { BookPageData, ProjectData } from '@/types';
import type { Container } from 'pixi.js';

/**
 * 将 PixiJS 颜色值统一转为 CSS hex 格式
 * 支持：数字 (0xFFFFFF)、'0x' 前缀字符串、标准 '#' 前缀
 * @param value 颜色值（数字或字符串）
 * @returns CSS hex 格式，如 '#ffffff'
 */
export function toHexColor(value: string | number): string {
  if (typeof value === 'number') {
    return '#' + value.toString(16).padStart(6, '0');
  }
  if (value.startsWith('0x')) {
    return '#' + value.slice(2);
  }
  return value;
}

/**
 * 为书页分配 index：
 * - type 为 cover 的页面固定排在最前（从 0 开始）
 * - type 为 backCover 的页面固定排在最后
 * - 其余页面按原数组中的相对顺序排在中间
 * 最后按 index 升序排序并返回
 * @param pages 书页数组
 * @returns 排序后的书页数组
 */
export function updateBookPageIndex(pages: BookPageData[]): BookPageData[] {
  const covers = pages.filter((p) => p.type === 'cover');
  const backCovers = pages.filter((p) => p.type === 'backCover');
  const normals = pages.filter((p) => p.type !== 'cover' && p.type !== 'backCover');

  covers.forEach((p, i) => {
    p.index = i;
  });
  normals.forEach((p, i) => {
    p.index = covers.length + i;
  });
  backCovers.forEach((p, i) => {
    p.index = covers.length + normals.length + i;
  });

  return [...covers, ...normals, ...backCovers].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
}
/**
 * 获取指定区域内，指定比例的最大区域
 * @param regionW 区域宽
 * @param regionH 区域高
 * @param whScale 目标区域的宽高比
 * @param isFull 是否超过指定区域，true：结果将铺满目标区域，false:结果目标区域内的最大值
 * @returns 返回width，height (都是四舍五入的整数)
 */
export function getScaleWH(
  regionW: number,
  regionH: number,
  whScale: number,
  isFull: boolean = false
) {
  const actualW = regionW;
  const actualH = regionH;

  let width = actualW;
  let height = width / whScale;
  if (isFull) {
    if (height < actualH) {
      height = actualH;
      width = height * whScale;
    }
  } else {
    if (height > actualH) {
      height = actualH;
      width = height * whScale;
    }
  }

  width = Math.round(Math.max(1, width));
  height = Math.round(Math.max(1, height));

  return { width, height };
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 获取元素所占据的区域信息
 * 注意：获取到的x,y是基于canvas本身的，也就是说0,0是左上角点
 * 根据需要，调用对应容器的toLocal方法来转换为容器内的坐标
 * @param elements
 * @returns
 */
export function getElementsBox(...elements: Container[]): Box {
  if (!elements || elements.length === 0) return { x: 0, y: 0, width: 0, height: 0 };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  elements.forEach((element) => {
    // 2. 获取元素相对于目标坐标系的边界
    // v8 中 getBounds 接收一个参数作为相对坐标系
    const bounds = element.getBounds();

    if (bounds.left < minX) minX = bounds.left;
    if (bounds.top < minY) minY = bounds.top;
    if (bounds.right > maxX) maxX = bounds.right;
    if (bounds.bottom > maxY) maxY = bounds.bottom;
  });

  const targetW = Number.isFinite(maxX - minX) ? maxX - minX : 100;
  const targetH = Number.isFinite(maxY - minY) ? maxY - minY : 100;

  return {
    x: minX,
    y: minY,
    width: targetW,
    height: targetH
  };
}

/**
 * 同步 ProjectData.book 中各页 action.element.layout 中的 parentWidth 和 height，
 * 使其与所在页的 width 和 height 保持一致。
 * @param projectData 工程数据
 */
export function syncPageLayoutDimensions(projectData: ProjectData): void {
  const pages = projectData.book?.pages;
  if (!pages) return;

  for (const page of pages) {
    const pageWidth = page.width;
    const pageHeight = page.height;
    if (pageWidth === undefined && pageHeight === undefined) continue;

    const actions = page.actions;
    if (!actions) continue;

    for (const action of actions) {
      const element = action.element;
      if (!element) continue;

      if (!element.attrs) {
        element.attrs = {};
      }

      if (!element.attrs.layout) {
        element.attrs.layout = {};
      }

      if (pageWidth !== undefined) {
        element.attrs.layout.parentWidth = pageWidth;
      }
      if (pageHeight !== undefined) {
        element.attrs.layout.parentHeight = pageHeight;
      }
    }
  }
}

/**
 * 将 HTMLCanvasElement 转换为 File
 */
export function canvasToFile(canvas: HTMLCanvasElement, fileName: string): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas toBlob 失败'));
        return;
      }
      const file = new File([blob], fileName, { type: 'image/png' });
      resolve(file);
    }, 'image/png');
  });
}

export function showTextEditor(textNode: TextElement, onEnd?: () => void) {
  if (!textNode) return;

  const textObj = textNode.textObj;
  const canvas = PixiGlobal.app?.canvas;
  const displayRatio = PixiGlobal.getDisplayRatio();

  if (!textObj || !canvas) return;

  const textStyle = textNode.styleData;
  const textAttrs = textNode.attrs;

  // 世界坐标下文本的实际渲染区域（包含 page.scale 等父级缩放），
  // 用左上角定位：TextElement 的 applyParentLayout 中头部位置由 layout.top
  // 固定，高度变化时只向下扩展，不会上下对称扩展。textarea 与之一致
  const worldBounds = textObj.getBounds();
  const worldScale = worldBounds.width / (textObj.width || 1);

  // 编辑框左上角坐标（canvas 父容器内 CSS 像素偏移）
  // setText 已保证元素始终向右向下扩展，编辑框只需锚定左上角即可
  const editorPos = {
    x: worldBounds.x * displayRatio,
    y: worldBounds.y * displayRatio
  };

  textNode.hide();

  // --- 字体样式 ---
  const fontFamily = textStyle.font?.fontFamily || 'Arial';
  const fontSize = (textStyle.font?.fontSize || 26) * worldScale * displayRatio;
  const rawFill = textStyle.fill?.fill ?? '#000000';
  const fontColor = toHexColor(Array.isArray(rawFill) ? rawFill[0] : rawFill);
  const fontWeight = textStyle.font?.fontWeight ?? 'normal';
  const fontStyle = textStyle.font?.fontStyle ?? 'normal';
  const letterSpacing = (textStyle.layout?.letterSpacing ?? 0) * worldScale * displayRatio;

  let lineHeight = fontSize;
  if (textStyle.layout?.lineHeight) {
    lineHeight = textStyle.layout.lineHeight * worldScale * displayRatio;
  }

  const align = textStyle.layout?.align || 'left';
  const rotation = textAttrs.rotation ?? 0;

  // 文字描边
  const stroke = textStyle.stroke;

  // 挂载到 .editor-canvas（canvas 的祖父，一层级之上），避开 .canvas-container
  // 的 overflow:hidden。挂载点仍在 .main-container 内，编辑框自然在工具栏之下
  const mountTarget = canvas.parentElement!.parentElement!;

  // 外层容器：position: absolute，contain: layout 防止撑大父级
  const editorLayer = document.createElement('div');
  Object.assign(editorLayer.style, {
    position: 'absolute',
    top: '0px',
    left: '0px',
    width: '100%',
    height: '100%',
    contain: 'layout style',
    overflow: 'visible',
    pointerEvents: 'none'
  });

  const textarea = document.createElement('textarea');
  editorLayer.appendChild(textarea);

  mountTarget.appendChild(editorLayer);

  // 将 PixiJS 层用于保护首行缩进的 NBSP 还原为普通空格，
  // 让 textarea 中用户看到和编辑的是普通空格
  textarea.value = textObj.text.replace(new RegExp(String.fromCharCode(0xa0), 'g'), ' ');
  textarea.style.position = 'absolute';
  textarea.style.pointerEvents = 'auto';
  textarea.style.left = editorPos.x + 'px';
  textarea.style.top = editorPos.y + 'px';
  textarea.style.width = `${worldBounds.width * displayRatio}px`;
  textarea.style.height = `${worldBounds.height * displayRatio}px`;
  textarea.style.fontSize = `${fontSize}px`;
  textarea.style.border = 'none';
  textarea.style.padding = '0px';
  textarea.style.margin = '0px';
  textarea.style.overflow = 'hidden';
  textarea.style.background = 'transparent';
  textarea.style.caretColor = '#000000';
  textarea.style.outline = 'none';
  textarea.style.resize = 'none';
  textarea.style.border = '2px solid #6033e1';

  // 换行策略
  textarea.style.wordBreak = 'break-all';
  textarea.style.whiteSpace = 'pre-wrap';
  textarea.style.letterSpacing = `${letterSpacing}px`;

  // 字体
  textarea.style.lineHeight = `${lineHeight * 1.2}px`;
  textarea.style.fontFamily = Array.isArray(fontFamily) ? fontFamily.join(', ') : fontFamily;
  textarea.style.fontWeight = String(fontWeight);
  textarea.style.fontStyle = fontStyle;
  textarea.style.textAlign = align;
  textarea.style.color = fontColor;

  // 文字描边
  if (stroke?.width) {
    const strokeColor = toHexColor(
      Array.isArray(stroke.color) ? stroke.color[0] : (stroke.color ?? '#000000')
    );
    textarea.style.webkitTextStrokeWidth = `${stroke.width * worldScale * displayRatio}px`;
    textarea.style.webkitTextStrokeColor = strokeColor;
    // fill 覆盖在 stroke 之上，避免粗描边吞没填充色
    textarea.style.paintOrder = 'stroke fill';
  }

  // 旋转：左上角定位后，绕自身中心旋转，匹配 PixiJS TextElement 绕中心旋转的行为
  if (rotation !== 0) {
    textarea.style.transform = `rotate(${rotation}deg)`;
  }

  // 初始高度设为 auto 后取 scrollHeight，确保与文本内容高度一致
  textarea.style.height = 'auto';
  textarea.style.height = textarea.scrollHeight + 3 + 'px';

  textarea.focus({ preventScroll: true });

  function removeTextarea() {
    if (editorLayer.parentNode) {
      editorLayer.parentNode.removeChild(editorLayer);
    }
    window.removeEventListener('click', handleOutsideClick);
    window.removeEventListener('resize', handleOutsideClick);
    textNode.show();
    onEnd?.();
  }

  function syncTextareaSize() {
    textarea.style.width = `${worldBounds.width * displayRatio}px`;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 3 + 'px';
  }

  textarea.addEventListener('keydown', function (e) {
    // if (e.key === 'Enter' && !e.shiftKey) {
    //   textNode.setText(textarea.value);
    //   removeTextarea();
    // }
    if (e.key === 'Escape') {
      removeTextarea();
    }
  });

  textarea.addEventListener('input', function () {
    syncTextareaSize();
  });

  function handleOutsideClick(e: Event) {
    if (e.target !== textarea) {
      textNode.setText(textarea.value);
      removeTextarea();
    }
  }

  requestAnimationFrame(() => {
    textarea.focus({ preventScroll: true });
  });

  setTimeout(() => {
    window.addEventListener('resize', handleOutsideClick);
    window.addEventListener('click', handleOutsideClick);
  }, 300);
}
